/**
 * Speech-to-text and text-to-speech API.
 *
 *   POST /api/speech-to-text   multipart `audio` → { text, language }
 *   POST /api/text-to-speech   { text, language? } → { audio_url, used_language }
 *
 * STT used by MicButton - record → upload → transcript fills the
 * composer textarea.
 *
 * TTS not actively called by D5a - assistant audio comes from the
 * `audio_url` field on /api/chat responses (server returns it
 * pre-baked when `audio_requested: true` was on the request).
 * Wrapper kept here for D5b/D5c if we add an "always speak this
 * message aloud" button later.
 */

import { ApiError } from './client';
import { api } from './client';

export interface TranscriptResponse {
  text: string;
  /** ISO 639-1 code, or '' when Whisper couldn't determine. */
  language: string;
}

export async function transcribeAudio(blob: Blob): Promise<TranscriptResponse> {
  const fd = new FormData();
  // Filename matters - handle_speech_to_text picks the temp-file
  // suffix from it. Use an explicit `.webm` since MediaRecorder
  // emits webm-Opus by default in modern browsers.
  fd.append('audio', blob, 'recording.webm');

  const res = await fetch('/api/speech-to-text', {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });

  let payload: unknown = null;
  try {
    payload = await res.json();
  } catch {
    /* fall through */
  }

  if (!res.ok) {
    const obj = (payload ?? {}) as { error?: string };
    throw new ApiError(
      res.status,
      'STT_FAILED',
      obj.error ?? `Speech-to-text failed (${res.status})`,
    );
  }

  return payload as TranscriptResponse;
}

export interface TtsRequest {
  text: string;
  /** Optional ISO 639-1 voice override. If omitted, server detects
   *  language from the text. */
  language?: string;
}

export interface TtsResponse {
  /** Same-origin URL like /audio/<filename>.mp3 - feed directly
   *  to <audio src>. */
  audio_url: string;
  used_language: string;
}

export function synthesizeSpeech(req: TtsRequest): Promise<TtsResponse> {
  return api<TtsResponse>('/api/text-to-speech', {
    method: 'POST',
    body: req,
  });
}

// ── Streaming STT (Google Cloud Speech-to-Text via WebSocket) ──────
//
// Thin wrapper around the /api/speech-to-text/stream WebSocket. The
// server-side wire protocol matches the widget's identical endpoint
// (routes/widget_api.py section header), same JSON frames, same
// fallback semantics. Only auth differs: this is session-cookie
// auth via Flask-Login (the WebSocket upgrade carries the cookie),
// no token query string needed.
//
// Lifecycle a caller drives:
//   1. const stream = openSttStream({ contentType, language });
//   2. await stream.opened;                  // waits for 'ready'
//   3. stream.onPartial(text => ...);        // optional UI hook
//   4. stream.send(chunkBlob);               // per MediaRecorder
//                                            // ondataavailable
//   5. const finalText = await stream.end(); // signal end + await final
//
// Any error (handshake refused, mid-stream Google failure, transport
// drop) rejects either `opened` or `end`. MicButton catches that and
// falls back to the existing POST endpoint with the recorded blob.

export type SttStreamEvent =
  | { type: 'ready' }
  | { type: 'partial'; text: string }
  | { type: 'final'; text: string; language?: string }
  | { type: 'error'; code?: string; error: string };

export class SttStream {
  private ws: WebSocket;
  /** Resolves when the server emits 'ready'; rejects on a pre-ready
   *  error (auth, encoding, NOT_STREAMING_PROVIDER) or transport close. */
  readonly opened: Promise<void>;
  private resolveOpen!: () => void;
  private rejectOpen!: (err: ApiError) => void;
  private resolveFinal: ((text: string) => void) | null = null;
  private rejectFinal: ((err: ApiError) => void) | null = null;
  private finalText = '';
  private finalLanguage = '';
  private partialCb: ((text: string) => void) | null = null;
  private isOpen = false;
  private closed = false;
  /** Audio frames produced before the WS handshake completes. Held
   *  and flushed in order once `ready` arrives so the first words of
   *  the user's utterance don't get dropped on slow handshakes. */
  private preReadyBuffer: ArrayBuffer[] = [];

  constructor(url: string) {
    this.opened = new Promise<void>((res, rej) => {
      this.resolveOpen = res;
      this.rejectOpen = rej;
    });

    this.ws = new WebSocket(url);
    this.ws.binaryType = 'arraybuffer';

    this.ws.addEventListener('message', (e) => this.onMessage(e));
    this.ws.addEventListener('error', () => {
      // The WS 'error' event carries no useful detail by spec; treat
      // it as a generic transport failure and let the close handler
      // settle the promises.
      if (!this.isOpen) {
        this.rejectOpen(
          new ApiError(0, 'STREAM_FAILED', 'WebSocket transport error'),
        );
      }
    });
    this.ws.addEventListener('close', () => {
      this.closed = true;
      if (!this.isOpen) {
        this.rejectOpen(
          new ApiError(0, 'STREAM_CLOSED', 'WebSocket closed before ready'),
        );
        return;
      }
      // After 'open' completed: if we're still waiting on a final,
      // settle it - either with whatever final text we've buffered,
      // or reject if none.
      if (this.rejectFinal && !this.finalText) {
        this.rejectFinal(
          new ApiError(0, 'NO_FINAL', 'Stream closed before a final transcript'),
        );
        this.rejectFinal = null;
        this.resolveFinal = null;
      } else if (this.resolveFinal && this.finalText) {
        this.resolveFinal(this.finalText);
        this.resolveFinal = null;
        this.rejectFinal = null;
      }
    });
  }

  /** Send an audio chunk. Buffers chunks produced before 'ready' so
   *  none of the user's audio is lost. No-op once the socket is closed. */
  send(chunk: Blob | ArrayBuffer | Uint8Array): void {
    if (this.closed) return;
    const dispatch = (buf: ArrayBuffer) => {
      if (this.isOpen && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(buf);
      } else {
        this.preReadyBuffer.push(buf);
      }
    };
    if (chunk instanceof ArrayBuffer) {
      dispatch(chunk);
    } else if (chunk instanceof Uint8Array) {
      const ab = chunk.buffer.slice(
        chunk.byteOffset,
        chunk.byteOffset + chunk.byteLength,
      ) as ArrayBuffer;
      dispatch(ab);
    } else {
      void chunk.arrayBuffer().then(dispatch).catch(() => { /* drop */ });
    }
  }

  /** Register a callback for interim transcripts. Most recent text
   *  wins; callers typically render this in the composer input. */
  onPartial(cb: (text: string) => void): void {
    this.partialCb = cb;
  }

  /** Signal end-of-audio and wait for the final transcript. */
  end(): Promise<string> {
    if (this.closed) {
      return this.finalText
        ? Promise.resolve(this.finalText)
        : Promise.reject(
            new ApiError(0, 'STREAM_CLOSED', 'Stream is already closed'),
          );
    }
    const p = new Promise<string>((res, rej) => {
      this.resolveFinal = res;
      this.rejectFinal = rej;
    });
    try {
      if (this.ws.readyState === WebSocket.OPEN) {
        this.ws.send('__END__');
      }
    } catch { /* server will see the close anyway */ }
    return p;
  }

  /** Tear down without awaiting a final. */
  cancel(): void {
    if (this.closed) return;
    this.closed = true;
    try { this.ws.close(); } catch { /* noop */ }
  }

  /** Best-effort detected language from the final frame (or ''). */
  get language(): string { return this.finalLanguage; }

  private onMessage(e: MessageEvent): void {
    if (typeof e.data !== 'string') return;
    let event: SttStreamEvent;
    try {
      event = JSON.parse(e.data) as SttStreamEvent;
    } catch {
      return;
    }
    switch (event.type) {
      case 'ready':
        this.isOpen = true;
        this.resolveOpen();
        for (const buf of this.preReadyBuffer) {
          try { this.ws.send(buf); } catch { /* drop */ }
        }
        this.preReadyBuffer = [];
        return;
      case 'partial':
        this.partialCb?.(event.text || '');
        return;
      case 'final':
        this.finalText = event.text || '';
        this.finalLanguage = event.language || '';
        if (this.resolveFinal) {
          this.resolveFinal(this.finalText);
          this.resolveFinal = null;
          this.rejectFinal = null;
        }
        return;
      case 'error': {
        const err = new ApiError(
          0,
          event.code || 'STREAM_FAILED',
          event.error || 'Streaming STT failed',
        );
        if (!this.isOpen) {
          this.rejectOpen(err);
        } else if (this.rejectFinal) {
          this.rejectFinal(err);
          this.resolveFinal = null;
          this.rejectFinal = null;
        }
        return;
      }
    }
  }
}

/** Open a streaming STT session to the dashboard endpoint. Session
 *  cookie is sent automatically by the browser on the WebSocket
 *  upgrade, so no token plumbing is needed. */
export function openSttStream(opts: {
  contentType: string;
  language?: string;
}): SttStream {
  const params = new URLSearchParams({ content_type: opts.contentType });
  if (opts.language) params.set('language', opts.language);
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const url = `${proto}//${window.location.host}/api/speech-to-text/stream?${params.toString()}`;
  return new SttStream(url);
}
